import React, { useState } from 'react';
import { get, isEmpty, has, isNumber, map } from 'lodash';

import { getLabel } from 'utils/getLabel';
import classnames from 'classnames';
import { BLANK_VALUE_SYMBOL, MIME_TYPES, GLOBAL_LABELS } from 'constants/common';
import { FileUploader } from 'modules/fileUploader';
import { FileUploadService } from 'services/fileUpload';
import { Icon } from 'modules/icon-bank';
import * as errorOverlay from 'utils/errorOverlay';
import { SERVICE_RESPONSE_STATUS } from 'constants/common';

/**
 * @type {object}
 * @desc A React presentation/dumb view component.
 * @example
 * import { fileField as FileField } from 'ui/views/generic/fileField';
 *
 * // render
 * <FileField propName='propValue'/>
 */

export const fileField = ( props ) => {

    // get selected value
    const selectedFile = get( props, 'selectedFile' );

    // state
    const [ state, setState ] = useState( {
        error: null,
        preview: props.preview,
        fileUploadService: new FileUploadService(),
    } );

    // accept mine types
    const accept = [];
    const allowedFileFormats = get( props, 'constraints.allowedFileFormats', '' ).split( ',' );

    if( ! isEmpty( allowedFileFormats ) ) {
        allowedFileFormats.forEach( allowedFileFormat => {
            const ext = allowedFileFormat.trim( ' ' );

            if( has( MIME_TYPES, ext ) ) {
                accept.push( MIME_TYPES[ ext ] );
            }
        } );
    }

    // maximum file size
    const maxSizeConstraint = get( props, 'constraints.maxSize' );
    const maxFileSize = ! isNumber( maxSizeConstraint ) ? undefined : parseInt( maxSizeConstraint );

    // image resolution constraint
    const resolutionConstraint = get( props, 'constraints.resolution', undefined );
    const aspectRatioConstraint = get( props, 'constraints.aspectRatio', undefined );

    // generic file error
    const genericFileError = getLabel( get( props, 'constraints.errorMessage' ) );
    const genericServerError = getLabel( GLOBAL_LABELS.DISPLAY_TEXT_SOMETHING_WENT_WRONG );

    /*-------------*/

    // dynamic class name
    const widgetClassName = classnames( 'ui-file-field', props.className, {
        'ui-file-field--disabled': props.disabled,
		'ui-file-field--readonly': props.readOnly,
    } );

    return (
        <div className={ widgetClassName }>

            <div className='ui-file-field__upload'>

                <div className='ui-file-field__upload__area'>

                    {/* trigger */}
                    <FileUploader
                        { ...state }
                        disabled={ props.disabled || props.readOnly }
                        accept={ isEmpty( accept ) ? undefined : accept }
                        multiple={ false }
                        maxSize={ maxFileSize }
                        description={ props.description }
                        dimensions={ resolutionConstraint }
                        aspectRatio={ aspectRatioConstraint }
                        service={ ( file ) => {
                            state.fileUploadService.cancel();
                            return state.fileUploadService.upload( { file, url: props.uploadUrl } );
                        } }
                        isDragAllowed={ get( props, 'constraints.isDragAllowed', false ) }
                        onCustomError={ ( error ) => setState( { ...state, error } ) }
                        onError={ () => setState( { ...state, error: genericFileError, showErrors: true } ) }
                        onUploadStart={ () => setState( { ...state, error: null, } ) }
                        onUploadSuccess={ ( response ) => {

                            if( response.body.status === SERVICE_RESPONSE_STATUS.SUCCESS ) {
                                
                                // fieldName, fieldValue
                                const fileId = get( response, 'body.component.data.uploadedFileId' );
                                const filePreview = get( response, 'body.component.data.uploadedFileUrl' );
                                const fileName  = filePreview.substr( filePreview.lastIndexOf( '/' ) + 1 );

                                // update state
                                setState( {
                                    ...state,
                                    error: null,
                                    preview: filePreview,
                                } );

                                // update form value
                                props.onChange( { ...props, fieldValue: fileId, fileName: fileName } );
                            } else {
                                errorOverlay.open();
                            }
                        } }

                        onUploadFailure={ () => {
                            setState( { ...state, error: genericServerError, } ); // props.onChange( { ...props, fieldValue: '', } ); // clear field value
                        } }
                    />
                    
                </div>
            </div>

            { ! isEmpty( selectedFile ) && (

                <div className='ui-file-field__upload__files'>

                    { map( selectedFile, ( file, index ) => {

                        const ext = file.substr( file.lastIndexOf( '.' ) + 1 );

                        return (
                            <div key={ index } className='ui-file-field__upload__files__title'>
                                <div className='ui-file-field__upload__files__title__extension'>
                                    { ext.toUpperCase() }
                                </div>
                                <div className='ui-file-field__upload__files__title__value'>
                                    { file }
                                </div>
                                { props.canDelete && (
                                    <div className='ui-file-field__upload__files__title__icon' onClick={ () => props.onFileRemove() }>
                                        <Icon name={ 'delete' } />
                                    </div>
                                ) }
                            </div>
                        );
                    } ) }
                </div>
            ) }

            {	// field error message
                state.showErrors &&  BLANK_VALUE_SYMBOL !== state.error  ? (
                    <div className = 'ui-file-field__error'>
                        { state.error }
                    </div>
                ) : null
            }

        </div>
    );
};

// set display name
fileField.displayName = 'fileField';

// set default props
fileField.defaultProps = {
    value: {},
    displayValue: {},
    name: '',
    preview: '',
    uploadUrl: '',
    onChange: '',
    constraints: {
        allowedFileFormats: ',',
        maxSize: '',
        resolution: '',
        aspectRatio: '',
        allowedMultipleFiles: '',
        errorMessage: ''
    },
    className: '',
    disabled: '',
    readOnly: '',
    id: '',
    title: '',
    required: '',
    placeholder: '',
    description: '',
    showErrors: '',
    errorMessage: ''
};

// prop types
fileField.propTypes = {};
